import { useEffect, useMemo, useRef, useState } from 'react';
import { client } from '../services/api';
import type { UserResponse } from '../services/api';
import { appendUserTypeToDisplayName } from '../constants/userTypes';
import { fetchIntakeAssignableUsers } from '../services/intakeAssignableUsers';
import { filterAssigneeUsers } from '../constants/assigneeFilters';

export function assigneeDisplayName(user: UserResponse): string {
  const first = (user.first_name ?? '').trim();
  const last = (user.last_name ?? '').trim();
  const full = `${first} ${last}`.trim();
  const base = full || user.email || user.id || 'Unknown';
  return appendUserTypeToDisplayName(base, user.user_type);
}

export interface IntakeAssignableUserOption {
  value: string;
  label: string;
}

export interface UseIntakeAssignableUsersReturn {
  users: UserResponse[];
  options: IntakeAssignableUserOption[];
  isLoading: boolean;
  error: Error | null;
}

// enabled: defer the fetch until the caller is ready (e.g. pass `open` from a
// dialog so we don't fire N requests when the dialog is mounted-but-closed per row).
export function useIntakeAssignableUsers(
  enabled = true
): UseIntakeAssignableUsersReturn {
  const [rawUsers, setRawUsers] = useState<UserResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // ref instead of state so toggling enabled on/off (e.g. dialog open/close)
  // doesn't re-trigger the effect and issue a redundant request.
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!enabled || hasFetched.current) return;
    hasFetched.current = true;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchIntakeAssignableUsers(client)
      .then(res => {
        if (!cancelled) setRawUsers(filterAssigneeUsers(res.data ?? []));
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('Failed to load intake assignable users:', err);
          setRawUsers([]);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const users = useMemo<UserResponse[]>(
    () =>
      [...rawUsers].sort((a, b) =>
        assigneeDisplayName(a).localeCompare(assigneeDisplayName(b))
      ),
    [rawUsers]
  );

  const options = useMemo<IntakeAssignableUserOption[]>(
    () => users.map(u => ({ value: u.id, label: assigneeDisplayName(u) })),
    [users]
  );

  return { users, options, isLoading, error };
}
